import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { absolutizeUrl } from "@/components/ImagePicker";
import { useLang } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";

export default function Guides() {
    const { t, lang } = useLang();
    const [items, setItems] = useState([]);

    useEffect(() => {
        api.get("/guide").then((r) => setItems(r.data)).catch(() => setItems([]));
    }, []);

    const pick = (g, key) => (lang === "en" && g[`${key}_en`]) ? g[`${key}_en`] : (g[key] || "");

    return (
        <div data-testid="guides-page" className="bg-abnormal-ink">
            <SEO
                title={lang === "en" ? "Argentario MTB Guides" : "Guide MTB Argentario"}
                description={lang === "en"
                    ? "Long-form guides to Monte Argentario MTB Enduro: how to plan, what to ride, when to go, what to eat."
                    : "Guide approfondite all'MTB Enduro di Monte Argentario: come pianificare, cosa cavalcare, quando andare, dove mangiare."}
                path="/guide"
            />
            <PageHeader
                label="TRAILS · GUIDE"
                title={t("guide.title1")}
                accent={t("guide.title2")}
                tagline={[lang === "en" ? "Plan" : "Pianifica", lang === "en" ? "Ride" : "Gira", lang === "en" ? "Eat" : "Mangia"]}
                sub={t("guide.subtitle")}
            />
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-14 md:pb-20">

            {items.length === 0 ? (
                <p className="mt-16 text-abnormal-paperDim label-mono">{t("guide.empty")}</p>
            ) : (
                <div data-testid="guides-list" className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((g) => {
                        const title = pick(g, "title");
                        const subtitle = pick(g, "subtitle");
                        const intro = pick(g, "intro");
                        return (
                        <Link
                            key={g.slug}
                            to={`/guide/${g.slug}`}
                            data-testid={`guide-card-${g.slug}`}
                            className="group bg-abnormal-charcoal border border-white/10 hover:border-abnormal-acid transition-colors overflow-hidden flex flex-col"
                        >
                            <div className="relative overflow-hidden h-56">
                                {g.hero_image && (
                                    <img src={absolutizeUrl(g.hero_image)} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                                {g.season && (
                                    <span className="absolute top-3 left-3 bg-black/70 border border-abnormal-acid/50 text-abnormal-acid label-mono px-2 py-1">
                                        {g.season}
                                    </span>
                                )}
                            </div>
                            <div className="p-6 flex-1 flex flex-col">
                                <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight text-abnormal-paper">{title}<span className="text-abnormal-acid">.</span></h3>
                                {subtitle && <p className="text-sm text-abnormal-paperDim mt-2">{subtitle}</p>}
                                {intro && <p className="text-sm text-abnormal-paperDim/70 mt-3 line-clamp-3 flex-1">{intro}</p>}
                                <span className="mt-5 inline-flex items-center gap-2 label-mono text-abnormal-acid font-bold group-hover:gap-3 transition-all">
                                    {t("guide.read")} <ArrowRight size={14} />
                                </span>
                            </div>
                        </Link>
                        );
                    })}
                </div>
            )}
            </div>
        </div>
    );
}
